import { useWindowSize } from '@vueuse/core';
import type { SpacingProp } from '@/types/Layout';

export const useLayout = () => {
  const { closeMenu, setBottomMenuHeight, openMenu } = useLayoutStore();

  const { topbar, header, horizontalMenu, bottomMenu, menuOpen } =
    storeToRefs(useLayoutStore());

  const { width: windowWidth } = useWindowSize();

  const isMobile = computed<boolean>(() => {
    return windowWidth.value < 768;
  });

  const isTablet = computed<boolean>(() => {
    return windowWidth.value >= 768 && windowWidth.value < 1024;
  });

  const isDesktop = computed<boolean>(() => {
    return windowWidth.value >= 1024;
  });

  const isLayoutMobileOrTablet = computed<boolean>(() => {
    return isMobile.value || isTablet.value;
  });

  const isTopBarVisible = computed<boolean>(() => {
    return !!topbar.value.isVisible;
  });

  const topBarHeight = computed<number>(() => {
    return topbar.value.height || 0;
  });

  const headerHeight = computed<number>(() => {
    return header.value.height || 0;
  });

  const horizontalMenuHeight = computed<number>(() => {
    return horizontalMenu.value.height || 0;
  });

  const bottomMenuHeight = computed<number>(() => {
    return bottomMenu.value.height || 0;
  });

  const isSidebarMenuOpen = computed(() => {
    return menuOpen.value;
  });

  function getSpacingMeasurementFromPropList(
    spacingProp: SpacingProp | undefined,
    defaultValue: number = 6,
  ): number {
    if (spacingProp === 'auto') {
      return 0;
    }

    if (!spacingProp) {
      return defaultValue;
    }

    if (Array.isArray(spacingProp)) {
      const normalizedArray = fillArray<number>(spacingProp);
      const [desktop, tablet, mobile] = normalizedArray;

      if (isMobile.value) {
        return mobile || defaultValue;
      } else if (isTablet.value) {
        return tablet || defaultValue;
      } else {
        return desktop || defaultValue;
      }
    }

    if (typeof spacingProp === 'object') {
      const entries = Object.entries(spacingProp);
      const keys = Object.keys(spacingProp);
      const data = Object.fromEntries(entries);
      const numberEntries = keys.filter((key) => !isNaN(Number(key)));

      for (const index in numberEntries) {
        const entry = numberEntries[index];

        if (windowWidth.value < Number(entry)) {
          return data[entry || ''] || defaultValue;
        }
      }

      if (data.mobile && isMobile.value) {
        return data.mobile;
      }

      if (data.tablet && isTablet.value) {
        return data.tablet;
      }

      if (data.desktop && isDesktop.value) {
        return data.desktop;
      }

      if (data.mobileOrTablet && isLayoutMobileOrTablet.value) {
        return data.mobileOrTablet;
      }

      return defaultValue;
    }

    if (typeof spacingProp === 'number') {
      return spacingProp;
    }

    return defaultValue;
  }

  const { appSource } = useParameters();

  // Testa `wv|webview` na user agent, ou seja WebView embarcada — na prática o
  // nosso app React Native. Chamava-se `isPwa` e o nome mentia (ENG-843): não
  // tem relação com PWA. O valor é o mesmo de antes, para `isStandAlone` e
  // `isAppSource` não mudarem.
  const isWebViewUserAgent = computed<boolean>(() => {
    const webViewUserAgents: RegExp = /wv|webview/gi;

    return webViewUserAgents.test(navigator.userAgent);
  });

  const isStandAlone = computed<boolean>(() => {
    const isInStandaloneMode = () => {
      return 'standalone' in window.navigator && window.navigator.standalone;
    };

    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      !!isInStandaloneMode() ||
      isWebViewUserAgent.value
    );
  });

  const isAppSource = computed<boolean>(() => {
    if (isStandAlone.value) {
      return true;
    }

    return appSource.value === 'app';
  });

  const isIosDevice = computed<boolean>(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();

    return /iphone|ipad|ipod/.test(userAgent);
  });

  const isIosSafari = computed<boolean>(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();

    const webkit = !!userAgent.match(/webkit/i);

    return isIosDevice.value && webkit && !userAgent.match(/crios/i);
  });

  watch(
    windowWidth,
    (newWidth, oldWidth) => {
      // Só atua quando há mudança real entre mobile e desktop
      if (oldWidth) {
        const wasDesktop = oldWidth >= 1024;
        const isNowDesktop = newWidth >= 1024;

        if (!wasDesktop && isNowDesktop) {
          // Mudou de mobile/tablet para desktop - abre o menu
          setBottomMenuHeight(0);
          openMenu();
        } else if (wasDesktop && !isNowDesktop) {
          // Mudou de desktop para mobile/tablet - fecha o menu
          closeMenu();
        }
      }

      // Na inicialização, só ajusta o bottomMenu se necessário
      if (!oldWidth && !isDesktop.value) {
        // Se for mobile na inicialização, não faz nada com o menu
      } else if (!oldWidth && isDesktop.value) {
        // Se for desktop na inicialização, só ajusta o bottomMenu
        setBottomMenuHeight(0);
      }
    },
    { immediate: true },
  );

  return {
    isMobile,
    isTablet,
    isDesktop,
    isLayoutMobileOrTablet,
    isTopBarVisible,
    topBarHeight,
    headerHeight,
    horizontalMenuHeight,
    bottomMenuHeight,
    isSidebarMenuOpen,
    getSpacingMeasurementFromPropList,
    isStandAlone,
    isAppSource,
    isIosDevice,
    isIosSafari,
  };
};
